import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ComponentChanges } from '../../../models/component-changes.model';
import { PopupPlacement } from '../../models/popup-placement.enum';

/**
 * The popup container component
 */
@Component({
  selector: 'ideom-popup-container',
  templateUrl: './popup-container.component.html',
  styleUrls: ['./popup-container.component.scss'],
  animations: [
    trigger('hiddenVisible', [
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateY(-10px)',
        })
      ),
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateY(0)',
        })
      ),
      transition('hidden <=> visible', [animate('0.1s')]),
    ]),
  ],
})
export class PopupContainerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  /**
   * The string content for popup
   */
  @Input() content: string;

  /**
   * The custom template for popup
   */
  @Input() template: TemplateRef<any>;

  /**
   * The position of host element
   */
  @Input() rect: DOMRect;

  /**
   * The scroll X offset
   */
  @Input() scrollX: number;

  /**
   * The scroll Y offset
   */
  @Input() scrollY: number;

  /**
   * The placement
   */
  @Input() placement: PopupPlacement;

  /**
   * The flag indicating that we should append popup to body
   */
  @Input() appendToBody = false;

  /**
   * The event emitter to emit close event
   */
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();

  /**
   * The container element
   */
  @ViewChild('container', { static: false }) container: ElementRef;

  /**
   * The top offset
   */
  @HostBinding('style.top.px') top: number;

  /**
   * The left offset
   */
  @HostBinding('style.left.px') left: number;

  /**
   * The right offset
   */
  @HostBinding('style.right.px') right: number;

  /**
   * The bottom offset
   */
  @HostBinding('style.bottom.px') bottom: number;

  /**
   * The visibility state for triggering animations
   */
  state: 'visible' | 'hidden' = 'hidden';

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  /**
   * @ignore
   */
  ngOnInit(): void {
    // not implemented
  }

  /**
   * @ignore
   */
  ngAfterViewInit(): void {
    this.state = 'visible';
    this.updatePosition();
    this.changeDetectorRef.detectChanges();
  }

  /**
   * @ignore
   * @param changes Component changes
   */
  ngOnChanges(changes: ComponentChanges<PopupContainerComponent>): void {
    if (changes.rect || changes.placement) {
      this.updatePosition();
    }
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    this.state = 'hidden';
  }

  /**
   * This method is used for updating popup position
   * @private
   */
  private updatePosition() {
    let top: number, bottom: number, left: number, right: number;
    if (this.container && this.rect && this.placement) {
      const containerRect = (this.container.nativeElement as Element).getBoundingClientRect();
      if (containerRect) {
        switch (this.placement) {
          case PopupPlacement.Top:
            if (this.appendToBody) {
              top = this.rect.top + this.scrollY - containerRect.height;
              left = this.rect.left + this.rect.width / 2 - containerRect.width / 2 + this.scrollX;
            } else {
              bottom = this.rect.height;
              left = this.rect.left + this.rect.width / 2 - containerRect.width / 2;
            }
            break;
          default:
          case PopupPlacement.Bottom:
            if (this.appendToBody) {
              top = this.rect.top + this.scrollY + this.rect.height;
              left = this.rect.left + this.scrollX + (this.rect.width / 2 - containerRect.width / 2);
            } else {
              top = this.rect.top + this.rect.height;
              left = this.rect.left + this.rect.width / 2 - containerRect.width / 2;
            }
            break;
          case PopupPlacement.Left:
            if (this.appendToBody) {
              top = this.rect.top + this.scrollY;
              right = this.rect.right + this.scrollX;
            } else {
              right = this.rect.right;
              top = -(this.rect.height / 2 + containerRect.height / 2);
            }
            break;
          case PopupPlacement.Right:
            if (this.appendToBody) {
              top = this.rect.top + this.scrollY;
              left = this.rect.left + this.rect.width + this.scrollX;
            } else {
              left = this.rect.left + this.rect.width;
              top = -(this.rect.height / 2 + containerRect.height / 2);
            }
            break;
        }

        // TODO: To prevent ExpressionChangedBeforeChecked error
        setTimeout(() => {
          this.top = top;
          this.bottom = bottom;
          this.left = left;
          this.right = right;
        });
      }
    }
  }
}
